import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import BasePage from '../authentication-layout';
import ProjectLogo from '../../../components/commons/ProjectLogo';
import { projectConfig } from '../../../config/projectConfig';
import {
  useAcceptInviteMutation,
  useGetInvitePreviewQuery,
} from '../../../redux/auth/authApi';

const MIN_PASSWORD_LENGTH = 12;
const MAX_PASSWORD_LENGTH = 64;

const getErrorMessage = (error: any, fallback: string) => {
  if (typeof error?.data?.message === 'string') {
    return error.data.message;
  }

  if (typeof error?.error === 'string') {
    return error.error;
  }

  return fallback;
};

const AcceptInvitePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitError, setSubmitError] = useState<string | undefined>();
  const [acceptInvite, { isLoading: isAccepting }] = useAcceptInviteMutation();
  const {
    data,
    isLoading: isLoadingInvite,
    error: inviteError,
  } = useGetInvitePreviewQuery(token, {
    skip: !token,
  });

  const invite = data?.invite;
  const previewErrorMessage = !token
    ? 'This invitation link is invalid. Please ask your administrator for a new invitation.'
    : inviteError
      ? getErrorMessage(
          inviteError,
          'We could not load this invitation. Please ask your administrator for a new invitation.'
        )
      : undefined;

  const expiresAtLabel = invite?.expiresAt
    ? new Date(String(invite.expiresAt)).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(undefined);

    if (!token) {
      setSubmitError('This invitation link is invalid. Please ask your administrator for a new invitation.');
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setSubmitError(`Your password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    if (password.length > MAX_PASSWORD_LENGTH) {
      setSubmitError(`Your password must be ${MAX_PASSWORD_LENGTH} characters or fewer.`);
      return;
    }

    if (password !== confirmPassword) {
      setSubmitError('Your passwords do not match.');
      return;
    }

    try {
      const response = await acceptInvite({ token, password }).unwrap();
      navigate(
        `/auth/login?invite=accepted${response.email ? `&email=${encodeURIComponent(response.email)}` : ''}`,
        { replace: true }
      );
    } catch (error) {
      setSubmitError(
        getErrorMessage(
          error,
          'We could not finish setting up your account. Please ask your administrator for a new invitation.'
        )
      );
    }
  };

  return (
    <BasePage image={projectConfig.bgLogin}>
      <Grid2 container spacing={1} justifyContent="center" alignItems="center" height="100%">
        <Grid2 size={{ xs: 11, sm: 9, md: 5, lg: 4, xl: 3 }}>
          <Card
            sx={theme => ({
              position: 'relative',
              borderRadius: theme.spacing(2),
              padding: theme.spacing(3, 0),
              boxShadow: `0px -2px 20px 2px ${theme.HEPalette.coloredShadows.dark}66`,
            })}
          >
            <Box width="100%" borderRadius="lg" textAlign="center">
              <Typography
                variant="h5"
                sx={theme => ({
                  color: theme.palette.primary.main,
                  mt: 1,
                  fontWeight: 'bold',
                })}
              >
                ACCEPT INVITATION
              </Typography>
              <Grid2 container justifyContent="center" sx={{ mt: 2, mb: 2 }}>
                <ProjectLogo size="card" />
              </Grid2>
            </Box>

            <Box px={3}>
              {previewErrorMessage ? (
                <Alert severity="error">{previewErrorMessage}</Alert>
              ) : isLoadingInvite ? (
                <Typography color="text.secondary">
                  Loading your invitation...
                </Typography>
              ) : (
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Stack spacing={1.5} sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Set your password to join
                      {' '}
                      <strong>{invite?.companyName || 'your workspace'}</strong>
                      {' '}
                      and finish creating your account.
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {invite?.role && <Chip size="small" label={`Role: ${invite.role}`} />}
                      {expiresAtLabel && (
                        <Chip size="small" variant="outlined" label={`Expires: ${expiresAtLabel}`} />
                      )}
                    </Stack>
                    {invite?.email && (
                      <Typography variant="body2" color="text.secondary">
                        Invitation for
                        {' '}
                        <strong>{invite.email}</strong>
                      </Typography>
                    )}
                  </Stack>

                  {submitError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {submitError}
                    </Alert>
                  )}

                  <TextField
                    margin="normal"
                    fullWidth
                    label="Create Password"
                    type="password"
                    value={password}
                    onChange={event => setPassword(event.target.value)}
                    required
                  />
                  <TextField
                    margin="normal"
                    fullWidth
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={event => setConfirmPassword(event.target.value)}
                    required
                  />

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Use 12 to 64 characters.
                  </Typography>

                  <Box mt={4} mb={1}>
                    <Button
                      type="submit"
                      disabled={isAccepting}
                      fullWidth
                      variant="contained"
                      sx={theme => ({
                        padding: theme.spacing(1, 0),
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      })}
                    >
                      {isAccepting ? 'Creating Account...' : 'Create Account'}
                    </Button>
                  </Box>

                  <Box textAlign="center" mt={2}>
                    <Typography variant="body2" color="text.secondary">
                      Already set up?
                      {' '}
                      <Link component={RouterLink} to="/auth/login" underline="hover">
                        Sign in
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Card>
        </Grid2>
      </Grid2>
    </BasePage>
  );
};

export default AcceptInvitePage;
